import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Clock,
  Check,
  AlertCircle,
  Play,
  RefreshCw,
  Plus,
  X,
  Calendar,
  ChevronDown,
  Search,
  FileText,
  Layers,
  ArrowLeft,
  Send,
  Copy,
  Trash2,
  Share2,
  ExternalLink,
  Loader2,
  Mail,
  Trello,
  MessageSquare,
  Share,
  ShoppingCart,
  Users,
  Code,
  DollarSign,
  Megaphone,
  LayoutGrid,
  Newspaper,
} from 'lucide-react';
import { AI_EMPLOYEES } from '../data/employees';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import ScheduledPosts from '../components/ScheduledPosts';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Simple markdown to formatted JSX converter
const formatMarkdownOutput = (text: string, isDark: boolean): React.ReactNode => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip horizontal rules (---)
    if (line.trim().match(/^-{3,}$/)) {
      elements.push(<div key={key++} className="my-4 border-t border-current opacity-20" />);
      continue;
    }

    // Headers (### Header) - render as bold
    const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      elements.push(
        <div key={key++} className="font-bold text-base mt-4 mb-2">
          {formatInlineMarkdown(headerMatch[1], isDark)}
        </div>
      );
      continue;
    }

    // Empty lines - add spacing
    if (line.trim() === '') {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    // Regular text - format inline markdown (bold, links)
    elements.push(
      <div key={key++} className="mb-1">
        {formatInlineMarkdown(line, isDark)}
      </div>
    );
  }

  return elements;
};

// Format inline markdown (bold text, links)
const formatInlineMarkdown = (text: string, isDark: boolean): React.ReactNode => {
  // Match **bold** and [link](url) patterns
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let partKey = 0;

  while (remaining.length > 0) {
    // Check for bold **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Check for links [text](url)
    const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

    // Find which comes first
    const boldIndex = boldMatch ? remaining.indexOf(boldMatch[0]) : -1;
    const linkIndex = linkMatch ? remaining.indexOf(linkMatch[0]) : -1;

    if (boldIndex === -1 && linkIndex === -1) {
      // No more patterns, add remaining text
      parts.push(<span key={partKey++}>{remaining}</span>);
      break;
    }

    // Determine which pattern comes first
    let firstIndex = -1;
    let matchType: 'bold' | 'link' | null = null;

    if (boldIndex !== -1 && (linkIndex === -1 || boldIndex < linkIndex)) {
      firstIndex = boldIndex;
      matchType = 'bold';
    } else if (linkIndex !== -1) {
      firstIndex = linkIndex;
      matchType = 'link';
    }

    // Add text before the match
    if (firstIndex > 0) {
      parts.push(<span key={partKey++}>{remaining.substring(0, firstIndex)}</span>);
    }

    // Add the matched element
    if (matchType === 'bold' && boldMatch) {
      parts.push(<span key={partKey++} className="font-semibold">{boldMatch[1]}</span>);
      remaining = remaining.substring(firstIndex + boldMatch[0].length);
    } else if (matchType === 'link' && linkMatch) {
      parts.push(
        <a
          key={partKey++}
          href={linkMatch[2]}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline ${isDark ? 'text-accent-cyan hover:text-accent-cyan/80' : 'text-accent-purple hover:text-accent-purple/80'}`}
        >
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.substring(firstIndex + linkMatch[0].length);
    }
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Automation Categories Definition
interface AutomationCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  color: string;
  image?: string;
  available: boolean;
}

const automationCategories: AutomationCategory[] = [
  {
    id: 'social-media',
    name: 'Social Media',
    description: 'Automate posting, scheduling, and engagement across all social platforms',
    icon: <Share className="w-6 h-6" />,
    gradient: 'from-pink-500 via-purple-500 to-indigo-500',
    color: 'bg-purple-500',
    available: true,
  },
  {
    id: 'communication',
    name: 'Communication',
    description: 'Streamline messaging across Slack, Discord, and email platforms',
    icon: <MessageSquare className="w-6 h-6" />,
    gradient: 'from-blue-500 via-cyan-500 to-teal-500',
    color: 'bg-blue-500',
    available: true,
  },
  {
    id: 'google-suite',
    name: 'Google Suite',
    description: 'Automate Gmail, Calendar, Drive, Docs, and Sheets workflows',
    icon: <Mail className="w-6 h-6" />,
    gradient: 'from-red-500 via-yellow-500 to-green-500',
    color: 'bg-red-500',
    available: true,
  },
  {
    id: 'daily-summarizer',
    name: 'Daily Summarizer',
    description: 'Get daily summaries of your emails and calendar events',
    icon: <Newspaper className="w-6 h-6" />,
    gradient: 'from-amber-500 via-yellow-500 to-orange-500',
    color: 'bg-amber-500',
    available: true,
  },
  {
    id: 'project-management',
    name: 'Project Management',
    description: 'Sync tasks and projects with Trello, Notion, and more',
    icon: <Trello className="w-6 h-6" />,
    gradient: 'from-blue-600 via-blue-500 to-cyan-400',
    color: 'bg-blue-600',
    available: false,
  },
  {
    id: 'e-commerce',
    name: 'E-Commerce',
    description: 'Manage products, orders, and inventory on Shopify',
    icon: <ShoppingCart className="w-6 h-6" />,
    gradient: 'from-green-500 via-emerald-500 to-teal-500',
    color: 'bg-green-500',
    available: false,
  },
  {
    id: 'crm-sales',
    name: 'CRM & Sales',
    description: 'Automate HubSpot and Salesforce workflows',
    icon: <Users className="w-6 h-6" />,
    gradient: 'from-orange-500 via-amber-500 to-yellow-500',
    color: 'bg-orange-500',
    available: false,
  },
  {
    id: 'development',
    name: 'Development',
    description: 'Integrate with GitHub for issues, PRs, and deployments',
    icon: <Code className="w-6 h-6" />,
    gradient: 'from-gray-700 via-gray-600 to-gray-500',
    color: 'bg-gray-700',
    available: false,
  },
  {
    id: 'finance',
    name: 'Finance',
    description: 'Handle payments and invoicing with Stripe and QuickBooks',
    icon: <DollarSign className="w-6 h-6" />,
    gradient: 'from-emerald-600 via-green-500 to-lime-500',
    color: 'bg-emerald-500',
    available: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Create email campaigns and manage subscribers with Mailchimp',
    icon: <Megaphone className="w-6 h-6" />,
    gradient: 'from-yellow-500 via-orange-500 to-red-500',
    color: 'bg-orange-400',
    available: false,
  },
];

interface Task {
  id: number;
  employee_id: string;
  use_case_id?: string;
  template_id?: string;
  title: string;
  description?: string;
  status: 'pending' | 'scheduled' | 'running' | 'completed' | 'failed' | 'cancelled';
  input_data?: string;
  output_data?: string;
  error_message?: string;
  credits_used: number;
  progress?: number;
  progress_message?: string;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
}

interface Stats {
  total: number;
  completed: number;
  running: number;
  pending: number;
  failed: number;
  total_credits_used: number;
}

interface TaskCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface TaskTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  employee_id: string;
  estimated_credits: number;
  tags: string[];
  input_fields?: InputField[];
  prompt_template?: string;
}

interface InputField {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'url' | 'email';
  placeholder?: string;
  required: boolean;
  options?: { value: string; label: string }[];
}

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface TasksResponse {
  items: Task[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface TemplatesResponse {
  categories: TaskCategory[];
  templates: TaskTemplate[];
  total: number;
}

type ViewMode = 'automations' | 'tasks' | 'templates' | 'template-form' | 'schedule';

interface Integration {
  id: number;
  provider: string;
  provider_name: string;
  provider_username?: string;
  provider_icon?: string;
  provider_color?: string;
  provider_category?: string;
  status: string;
}

// Mapping of automation category IDs to integration provider IDs
// Note: Google Suite should NOT include Gmail (Gmail is communication only)
const CATEGORY_TO_PROVIDER_IDS: Record<string, string[]> = {
  'social-media': ['twitter', 'facebook', 'linkedin', 'instagram', 'tiktok', 'pinterest', 'youtube'],
  'communication': ['gmail', 'slack', 'discord', 'outlook'],
  'google-suite': ['google_calendar', 'google_drive', 'google_docs', 'google_sheets'],
  // Daily Summarizer should be enabled if ANY data source is connected (social media, communication, or calendar)
  'daily-summarizer': [
    // Social media
    'twitter', 'facebook', 'linkedin', 'instagram', 'tiktok', 'pinterest', 'youtube',
    // Communication
    'gmail', 'slack', 'discord', 'outlook',
    // Calendar
    'google_calendar',
  ],
};

export default function Tasks() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'scheduled' | 'running' | 'completed' | 'failed'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Templates state
  const [viewMode, setViewMode] = useState<ViewMode>('automations');
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [templateInputs, setTemplateInputs] = useState<Record<string, string>>({});
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Send to integration state
  const [showSendModal, setShowSendModal] = useState(false);
  const [taskToSend, setTaskToSend] = useState<Task | null>(null);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  // Connection status for automation categories
  const [categoryConnectionStatus, setCategoryConnectionStatus] = useState<Record<string, boolean>>({});

  // New task form
  const [newTask, setNewTask] = useState({
    employee_id: '',
    title: '',
    description: '',
    scheduled_at: '',
  });

  const loadTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);

      const response = await fetch(`${API_URL}/tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<TasksResponse>;
      if (data.success && data.data) {
        setTasks(data.data.items || []);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  }, [token, filter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/tasks/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Stats>;
      if (data.success && data.data) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [token]);

  const loadTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (templateSearch) params.append('search', templateSearch);

      const response = await fetch(`${API_URL}/tasks/templates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<TemplatesResponse>;
      if (data.success && data.data) {
        setTemplates(data.data.templates || []);
        if (!categories.length && data.data.categories) {
          setCategories(data.data.categories);
        }
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [token, selectedCategory, templateSearch, categories.length]);

  const loadTemplateDetails = async (templateId: string) => {
    try {
      const response = await fetch(`${API_URL}/tasks/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<TaskTemplate>;
      if (data.success && data.data) {
        setSelectedTemplate(data.data);
        setTemplateInputs({});
        setViewMode('template-form');
      }
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  // Load connection status for automation categories
  const loadCategoryConnectionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Integration[]>;

      if (data.success && data.data) {
        // Get set of connected provider IDs
        const connectedProviderIds = new Set<string>();

        for (const integration of data.data) {
          if (integration.status === 'connected') {
            connectedProviderIds.add(integration.provider);
          }
        }

        // Check each automation category to see if any of its specific providers are connected
        const categoryStatus: Record<string, boolean> = {};
        for (const [automationCategory, providerIds] of Object.entries(CATEGORY_TO_PROVIDER_IDS)) {
          categoryStatus[automationCategory] = providerIds.some(
            providerId => connectedProviderIds.has(providerId)
          );
        }

        setCategoryConnectionStatus(categoryStatus);
      }
    } catch (error) {
      console.error('Failed to load category connection status:', error);
    }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadTasks(), loadStats(), loadCategoryConnectionStatus()]);
      setIsLoading(false);
    };
    init();
  }, [loadTasks, loadStats, loadCategoryConnectionStatus]);

  useEffect(() => {
    loadTasks();
  }, [filter, loadTasks]);

  useEffect(() => {
    if (viewMode === 'templates') {
      loadTemplates();
    }
  }, [viewMode, loadTemplates]);

  // Poll for running tasks
  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'running' || t.status === 'pending');
    if (!hasRunning) return;

    const interval = setInterval(() => {
      loadTasks();
      loadStats();
    }, 3000);

    return () => clearInterval(interval);
  }, [tasks, loadTasks, loadStats]);

  const createTask = async () => {
    if (!newTask.employee_id || !newTask.title) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employee_id: newTask.employee_id,
          title: newTask.title,
          description: newTask.description || undefined,
          scheduled_at: newTask.scheduled_at || undefined,
        }),
      });

      const data = (await response.json()) as APIResponse<Task>;
      if (data.success && data.data) {
        setTasks(prev => [data.data!, ...prev]);
        setShowCreateModal(false);
        setNewTask({ employee_id: '', title: '', description: '', scheduled_at: '' });
        loadStats();
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const createTaskFromTemplate = async () => {
    if (!selectedTemplate) return;

    setIsCreating(true);
    try {
      const response = await fetch(`${API_URL}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          input_data: templateInputs,
        }),
      });

      const data = (await response.json()) as APIResponse<Task>;
      if (data.success && data.data) {
        setTasks(prev => [data.data!, ...prev]);
        setViewMode('tasks');
        setSelectedTemplate(null);
        setTemplateInputs({});
        loadStats();
      }
    } catch (error) {
      console.error('Failed to create task from template:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const cancelTask = async (taskId: number) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<Task>;
      if (data.success) {
        loadTasks();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to cancel task:', error);
    }
  };

  const retryTask = async (taskId: number) => {
    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}/retry`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<unknown>;
      if (data.success) {
        loadTasks();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to retry task:', error);
    }
  };

  const deleteTask = async (taskId: number) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = (await response.json()) as APIResponse<unknown>;
      if (data.success) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        setSelectedTask(null);
        loadStats();
      }
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const copyOutput = (task: Task) => {
    if (task.output_data) {
      try {
        const output = JSON.parse(task.output_data);
        navigator.clipboard.writeText(output.result || '');
      } catch {
        navigator.clipboard.writeText(task.output_data);
      }
    }
  };

  const loadIntegrations = async () => {
    setIsLoadingIntegrations(true);
    try {
      const response = await fetch(`${API_URL}/integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await response.json()) as APIResponse<Integration[]>;
      if (data.success && data.data) {
        // Filter only connected integrations that support posting
        const postableProviders = ['twitter', 'facebook', 'linkedin', 'slack', 'discord', 'instagram', 'gmail'];
        const connected = data.data.filter(
          (i) => i.status === 'connected' && postableProviders.includes(i.provider)
        );
        setIntegrations(connected);
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    } finally {
      setIsLoadingIntegrations(false);
    }
  };

  const openSendModal = (task: Task) => {
    setTaskToSend(task);
    setShowSendModal(true);
    setSendSuccess(null);
    setSendError(null);
    loadIntegrations();
  };

  const sendToIntegration = async (integrationId: number, provider: string) => {
    if (!taskToSend?.output_data) return;

    setIsSending(true);
    setSendError(null);
    setSendSuccess(null);

    try {
      let content = '';
      try {
        const output = JSON.parse(taskToSend.output_data);
        content = output.result || taskToSend.output_data;
      } catch {
        content = taskToSend.output_data;
      }

      const response = await fetch(`${API_URL}/tasks/${taskToSend.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          integration_id: integrationId,
          provider,
          content,
        }),
      });

      const data = (await response.json()) as APIResponse<{ message: string }>;
      if (data.success) {
        setSendSuccess(`Successfully sent to ${provider}!`);
        setTimeout(() => {
          setShowSendModal(false);
          setTaskToSend(null);
          setSendSuccess(null);
        }, 2000);
      } else {
        setSendError(data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Failed to send to integration:', error);
      setSendError('Failed to send. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const getProviderIcon = (provider: string): string => {
    const icons: Record<string, string> = {
      twitter: '𝕏',
      facebook: 'f',
      linkedin: 'in',
      instagram: '📷',
      slack: '#',
      discord: '🎮',
      gmail: '✉',
    };
    return icons[provider] || '🔗';
  };

  const getProviderColor = (provider: string): string => {
    const colors: Record<string, string> = {
      twitter: 'from-slate-800 to-slate-900',
      facebook: 'from-blue-600 to-blue-700',
      linkedin: 'from-blue-500 to-blue-600',
      instagram: 'from-pink-500 to-purple-600',
      slack: 'from-purple-500 to-pink-500',
      discord: 'from-indigo-500 to-purple-600',
      gmail: 'from-red-500 to-orange-500',
    };
    return colors[provider] || 'from-gray-500 to-gray-600';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-accent-green/20 text-accent-green';
      case 'running':
        return 'bg-accent-cyan/20 text-accent-cyan';
      case 'pending':
      case 'scheduled':
        return 'bg-accent-orange/20 text-accent-orange';
      case 'failed':
        return 'bg-accent-red/20 text-accent-red';
      case 'cancelled':
        return 'bg-dark-700 text-text-muted';
      default:
        return 'bg-dark-700 text-text-muted';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Check className="w-3 h-3" />;
      case 'running':
        return <RefreshCw className="w-3 h-3 animate-spin" />;
      case 'pending':
        return <Clock className="w-3 h-3" />;
      case 'scheduled':
        return <Calendar className="w-3 h-3" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3" />;
      case 'cancelled':
        return <X className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getCategoryColor = (color: string) => {
    const colors: Record<string, string> = {
      purple: 'from-accent-purple to-accent-purple/50',
      blue: 'from-blue-500 to-blue-500/50',
      pink: 'from-pink-500 to-pink-500/50',
      green: 'from-accent-green to-accent-green/50',
      cyan: 'from-accent-cyan to-accent-cyan/50',
      orange: 'from-accent-orange to-accent-orange/50',
      yellow: 'from-yellow-500 to-yellow-500/50',
      teal: 'from-teal-500 to-teal-500/50',
      indigo: 'from-indigo-500 to-indigo-500/50',
      emerald: 'from-emerald-500 to-emerald-500/50',
      slate: 'from-slate-500 to-slate-500/50',
      violet: 'from-violet-500 to-violet-500/50',
    };
    return colors[color] || colors.purple;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Separate available and coming soon categories
  const availableCategories = automationCategories.filter(c => c.available);
  const comingSoonCategories = automationCategories.filter(c => !c.available);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center">
        <h1 className={`text-2xl sm:text-4xl font-bold mb-1 sm:mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Automations</h1>
        <p className={`text-sm sm:text-base ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
          Automate your workflows with AI-powered integrations
        </p>
      </motion.div>

      {/* View Content */}
      <AnimatePresence mode="wait">
        {viewMode === 'automations' && (
          <motion.div
            key="automations"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* Available Automations */}
            <div className="flex flex-col items-center">
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Available Automations
              </h2>
              <div className="flex flex-col gap-6 w-full max-w-xl">
                {availableCategories.map((category) => {
                  const isConnected = categoryConnectionStatus[category.id] || false;

                  return (
                    <motion.div
                      key={category.id}
                      className="automation-card cursor-pointer"
                      onClick={() => {
                        // Navigate to specific category page
                        if (category.id === 'social-media') {
                          window.location.href = '/dashboard/social-media-manager';
                        } else if (category.id === 'communication') {
                          window.location.href = '/dashboard/communication';
                        } else if (category.id === 'google-suite') {
                          window.location.href = '/dashboard/google-suite';
                        } else if (category.id === 'daily-summarizer') {
                          window.location.href = '/dashboard/daily-summarizer';
                        } else {
                          window.location.href = '/dashboard/integrations';
                        }
                      }}
                    >
                      {/* Content */}
                      <div className={`relative p-6 h-full min-h-[180px] flex flex-col rounded-2xl border ${
                        isDark
                          ? 'bg-dark-800 border-white/10'
                          : 'bg-white border-slate-200'
                      }`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {/* Icon - always colored */}
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${category.color}`}>
                              <span className="text-xl text-white">{category.icon}</span>
                            </div>
                            {/* Title - always normal color */}
                            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {category.name}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Toggle - green and ON if connected, gray and OFF if not */}
                            <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                              isConnected
                                ? 'bg-green-500'
                                : (isDark ? 'bg-gray-700' : 'bg-slate-300')
                            }`}>
                              <div className={`w-4 h-4 bg-white rounded-full transition-all ${
                                isConnected ? 'ml-auto' : ''
                              }`} />
                            </div>
                          </div>
                        </div>

                        <p className={`text-sm mb-4 flex-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                          {category.description}
                        </p>

                        <div className="flex items-center justify-end">
                          <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                            Click to {isConnected ? 'configure' : 'connect'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Coming Soon Automations */}
            <div className="flex flex-col items-center">
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Coming Soon
              </h2>
              <div className="flex flex-col gap-6 w-full max-w-xl">
                {comingSoonCategories.map((category) => (
                  <motion.div
                    key={category.id}
                    className="automation-card-disabled"
                  >
                    {/* Content */}
                    <div className={`relative p-6 h-full min-h-[180px] flex flex-col rounded-2xl border ${
                      isDark
                        ? 'bg-dark-800 border-white/5'
                        : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isDark ? 'bg-gray-700' : 'bg-slate-200'
                          }`}>
                            <span className={`text-xl ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>{category.icon}</span>
                          </div>
                          <h3 className={`text-xl font-bold ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                            {category.name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-12 h-6 rounded-full flex items-center px-1 ${
                            isDark ? 'bg-gray-700' : 'bg-slate-300'
                          }`}>
                            <div className={`w-4 h-4 rounded-full ${isDark ? 'bg-gray-600' : 'bg-slate-400'}`} />
                          </div>
                        </div>
                      </div>

                      <p className={`text-sm mb-4 flex-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                        {category.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          isDark
                            ? 'bg-gray-700 text-gray-400'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          Coming Soon
                        </span>
                        <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>
                          Stay tuned
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-2xl p-6 max-w-md w-full border ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200 shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Create New Task</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Employee Select */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  AI Employee
                </label>
                <div className="relative">
                  <select
                    value={newTask.employee_id}
                    onChange={(e) => setNewTask(prev => ({ ...prev, employee_id: e.target.value }))}
                    className={`w-full border rounded-xl px-4 py-3 appearance-none cursor-pointer focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                  >
                    <option value="">Select an employee</option>
                    {AI_EMPLOYEES.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} - {emp.role}</option>
                    ))}
                  </select>
                  <ChevronDown className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Task Title
                </label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Generate weekly social media content"
                  className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Description (optional)
                </label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add more details about what you want..."
                  rows={3}
                  className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none resize-none ${isDark ? 'bg-dark-900 border-dark-600 text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                />
              </div>

              {/* Schedule */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Schedule (optional)
                </label>
                <input
                  type="datetime-local"
                  value={newTask.scheduled_at}
                  onChange={(e) => setNewTask(prev => ({ ...prev, scheduled_at: e.target.value }))}
                  className={`w-full border rounded-xl px-4 py-3 focus:border-accent-purple focus:outline-none ${isDark ? 'bg-dark-900 border-dark-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={createTask}
                  disabled={!newTask.employee_id || !newTask.title || isCreating}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : (
                    'Create Task'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-2xl p-6 max-w-2xl w-full border max-h-[80vh] overflow-y-auto ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200 shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{selectedTask.title}</h2>
              <button
                onClick={() => setSelectedTask(null)}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-4">
                <span className={`badge flex items-center gap-1 ${getStatusColor(selectedTask.status)}`}>
                  {getStatusIcon(selectedTask.status)}
                  {selectedTask.status}
                </span>
                <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  {selectedTask.credits_used || 0} credits used
                </span>
              </div>

              {/* Progress */}
              {selectedTask.status === 'running' && (
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{selectedTask.progress_message || 'Processing...'}</span>
                    <span className="text-sm text-accent-cyan">{selectedTask.progress || 0}%</span>
                  </div>
                  <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                    <div
                      className="h-full bg-accent-cyan transition-all duration-500"
                      style={{ width: `${selectedTask.progress || 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Output */}
              {selectedTask.status === 'completed' && selectedTask.output_data && (
                <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className={`text-sm font-medium ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Output</h3>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => copyOutput(selectedTask)}
                        className="text-sm text-accent-purple hover:text-accent-purple/80"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTask(null);
                          openSendModal(selectedTask);
                        }}
                        className="text-sm text-accent-cyan hover:text-accent-cyan/80 flex items-center gap-1"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Send
                      </button>
                    </div>
                  </div>
                  <div className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {(() => {
                      try {
                        const output = JSON.parse(selectedTask.output_data);
                        const result = output.result || 'No output';
                        return formatMarkdownOutput(result, isDark);
                      } catch {
                        return formatMarkdownOutput(selectedTask.output_data, isDark);
                      }
                    })()}
                  </div>
                </div>
              )}

              {/* Error */}
              {selectedTask.error_message && (
                <div className="bg-accent-red/10 border border-accent-red/20 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-accent-red mb-1">Error</h3>
                  <p className="text-sm text-accent-red/80">{selectedTask.error_message}</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>Created:</span>
                  <span className={`ml-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatDate(selectedTask.created_at)}</span>
                </div>
                {selectedTask.completed_at && (
                  <div>
                    <span className={isDark ? 'text-text-muted' : 'text-slate-500'}>Completed:</span>
                    <span className={`ml-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatDate(selectedTask.completed_at)}</span>
                  </div>
                )}
              </div>

              {/* Delete Button */}
              <div className="pt-4 border-t border-border mt-4">
                <button
                  onClick={() => deleteTask(selectedTask.id)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-accent-red hover:bg-accent-red/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Task
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Send to Integration Modal */}
      {showSendModal && taskToSend && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-2xl p-6 max-w-md w-full border ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200 shadow-xl'}`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Send to Integration</h2>
              <button
                onClick={() => {
                  setShowSendModal(false);
                  setTaskToSend(null);
                  setSendSuccess(null);
                  setSendError(null);
                }}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
              >
                <X className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
              </button>
            </div>

            {/* Task Preview */}
            <div className={`rounded-xl p-4 mb-4 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
              <p className={`text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{taskToSend.title}</p>
              <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                {(() => {
                  try {
                    const output = JSON.parse(taskToSend.output_data || '{}');
                    const result = output.result || '';
                    return result.substring(0, 100) + (result.length > 100 ? '...' : '');
                  } catch {
                    return (taskToSend.output_data || '').substring(0, 100);
                  }
                })()}
              </p>
            </div>

            {/* Success Message */}
            {sendSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-accent-green/10 border border-accent-green/20">
                <p className="text-sm text-accent-green flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {sendSuccess}
                </p>
              </div>
            )}

            {/* Error Message */}
            {sendError && (
              <div className="mb-4 p-3 rounded-lg bg-accent-red/10 border border-accent-red/20">
                <p className="text-sm text-accent-red flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {sendError}
                </p>
              </div>
            )}

            {/* Loading Integrations */}
            {isLoadingIntegrations && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-accent-purple" />
              </div>
            )}

            {/* No Integrations */}
            {!isLoadingIntegrations && integrations.length === 0 && (
              <div className="text-center py-8">
                <Share2 className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                <p className={`text-sm mb-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  No connected integrations
                </p>
                <p className={`text-xs mb-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                  Connect your social media accounts or apps to send task outputs directly.
                </p>
                <a
                  href="/dashboard/integrations"
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Connect Integrations
                </a>
              </div>
            )}

            {/* Integration List */}
            {!isLoadingIntegrations && integrations.length > 0 && !sendSuccess && (
              <div className="space-y-2">
                <p className={`text-sm mb-3 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  Select where to send this content:
                </p>
                {integrations.map((integration) => (
                  <button
                    key={integration.id}
                    onClick={() => sendToIntegration(integration.id, integration.provider)}
                    disabled={isSending}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      isDark
                        ? 'bg-dark-900 border-dark-600 hover:border-accent-purple/50'
                        : 'bg-white border-slate-200 hover:border-accent-purple/50'
                    } ${isSending ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getProviderColor(integration.provider)} flex items-center justify-center text-white font-bold`}>
                      {getProviderIcon(integration.provider)}
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {integration.provider_name || integration.provider}
                      </p>
                      {integration.provider_username && (
                        <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                          @{integration.provider_username}
                        </p>
                      )}
                    </div>
                    {isSending ? (
                      <Loader2 className="w-5 h-5 animate-spin text-accent-purple" />
                    ) : (
                      <Send className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                    )}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      )}

    </motion.div>
  );
}
